import * as signalR from "@microsoft/signalr";
import * as msgpack from "@microsoft/signalr-protocol-msgpack";

/**
 * Configuration settings for the WebSocket service.
 */
export interface IConfig {
    /**
     * The URL of the WebSocket service.
     */
    notificationsUrl: string;
    apiBaseUrl: string;
    isProduction: boolean;
}

/**
 * Base class for managing WebSocket connections using SignalR.
 * @template T The type of data received from the server.
 */
export abstract class BaseWebSocketService<T> {
    protected connection: signalR.HubConnection;
    private keepAliveIntervalInMilliseconds: number = 1000 * 60 * 3; // Three minutes
    private serverTimeoutInMilliseconds: number = 1000 * 60 * 6; // Six minutes
    protected joinGroupQueue: string[] = []; // Queue for groups to join once connected

    /**
     * Initializes a new instance of the WebSocket service.
     * @param {IConfig} configuration Configuration settings for the WebSocket service.
     * @param {boolean} [useMsgPack=false] Indicates whether to use MessagePack protocol for communication.
     */
    constructor(protected configuration: IConfig, useMsgPack: boolean = false) {
        this.connection = this.createHubConnection(configuration.notificationsUrl, useMsgPack);
        this.startConnections();
    }

    /**
     * Creates a SignalR hub connection.
     * @param {string} url The URL of the hub.
     * @param {boolean} useMsgPack Indicates whether to use MessagePack protocol.
     * @returns {signalR.HubConnection} The created hub connection.
     */
    private createHubConnection(url: string, useMsgPack: boolean): signalR.HubConnection {
        const builder = new signalR.HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Debug);

        if (useMsgPack) {
            builder.withHubProtocol(new msgpack.MessagePackHubProtocol());
        }

        const connection = builder.build();
        connection.keepAliveIntervalInMilliseconds = this.keepAliveIntervalInMilliseconds;
        connection.serverTimeoutInMilliseconds = this.serverTimeoutInMilliseconds;
        return connection;
    }

    /**
     * Starts the connection to the hub and processes any queued group joins.
     */
    protected async startConnections(): Promise<void> {
        try {
            await this.connection.start();
            console.log("Connection started");
            this.processJoinGroupQueue();
        } catch (error) {
            console.error("Failed to start the connection:", error);
        }
    }

    /**
     * Processes the join group queue by joining each group in the queue.
     */
    private async processJoinGroupQueue(): Promise<void> {
        while (this.joinGroupQueue.length > 0) {
            const group = this.joinGroupQueue.shift();
            if (group) {
                await this.joinGroup(group);
            }
        }
    }

    /**
     * Joins a specified group on the hub connection.
     * @param {string} group The name of the group to join.
     */
    protected async joinGroup(group: string): Promise<void | T | boolean> {
        if (this.connection.state === signalR.HubConnectionState.Connected) {
            await this.connection.invoke("JoinGroup", group);
            console.log(`Joined group ${group}`);
            return true;
        } else {
            this.joinGroupQueue.push(group);
            console.log(`Connection not established. Queuing group ${group} to join.`);
            return false;
        }
    }

    /**
     * Leaves a specified group on the hub connection.
     * @param {string} group The name of the group to leave.
     */
    protected async leaveGroup(group: string): Promise<void> {
        await this.connection.invoke("LeaveGroup", group);
        console.log(`Left group ${group}`);
    }

    /**
     * Abstract method to be implemented by derived classes for handling received data.
     * @param {T} data The data received from the server.
     */
    public abstract onReceive(data: T): void;

    /**
     * Stops the hub connection.
     */
    public async stop(): Promise<void> {
        await this.connection.stop();
    }
}
